import { QueryKey } from '@/config/query-client';
import { useQuery } from '@tanstack/react-query';
import { getTimeInMs } from '@/helpers/get-time-in-ms';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import {
  fetchSpotlightCompanies,
  fetchSpotlightCompaniesKpis,
} from '@/api/v4/spotlight.api';

export type SpotlightCompanySortMode = 'alphabetical' | 'lastAdded';

interface SpotlightCompaniesFilters {
  isEditorChoice?: boolean;
  listingStages?: string[];
}

const normalizeSearch = (search: string): string | undefined => {
  const trimmedSearch = search.trim();

  return trimmedSearch.length > 0 ? trimmedSearch : undefined;
};

const getSortParams = (sortMode: SpotlightCompanySortMode) => {
  if (sortMode === 'lastAdded') {
    return {
      direction: 'desc' as const,
      sort: 'createdDate' as const,
    };
  }

  return {
    direction: 'asc' as const,
    sort: 'name' as const,
  };
};

export const useSpotlightCompanies = (
  page: number,
  pageSize: number,
  search: string,
  sortMode: SpotlightCompanySortMode,
  filters: SpotlightCompaniesFilters,
  isFilterMappingReady = true,
) => {
  const { hasRequiredPermission } = useUserPermissions();
  const listingStages = filters.listingStages
    ? [...filters.listingStages].sort()
    : [];
  const normalizedSearch = normalizeSearch(search);
  const sortParams = getSortParams(sortMode);
  const hasEditorChoice = Boolean(filters.isEditorChoice);
  const hasCompanyListPermission = hasRequiredPermission(
    PERMISSION.VIEW_ORGANIZATION_LIST,
  );
  const hasListingStages = listingStages.length > 0;
  const isFilterSelectionReady =
    (hasEditorChoice || hasListingStages) && isFilterMappingReady;

  return useQuery({
    queryKey: [
      QueryKey.Companies,
      'spotlight',
      normalizedSearch,
      Boolean(normalizedSearch),
      page,
      pageSize,
      sortMode,
      filters.isEditorChoice ?? false,
      listingStages.join(','),
    ],
    queryFn: () =>
      fetchSpotlightCompanies({
        ...(filters.isEditorChoice ? { isEditorChoice: true } : {}),
        ...(hasListingStages ? { listingStages } : {}),
        direction: sortParams.direction,
        page,
        search: normalizedSearch,
        size: pageSize,
        sort: sortParams.sort,
      }),
    enabled: hasCompanyListPermission && isFilterSelectionReady,
    staleTime: getTimeInMs(5, 'minute'),
  });
};

export const useSpotlightCompaniesKpi = (
  search: string,
  filters: SpotlightCompaniesFilters,
  isFilterMappingReady = true,
) => {
  const { hasRequiredPermission } = useUserPermissions();
  const listingStages = filters.listingStages
    ? [...filters.listingStages].sort()
    : [];
  const normalizedSearch = normalizeSearch(search);
  const hasEditorChoice = Boolean(filters.isEditorChoice);
  const hasCompanyListPermission = hasRequiredPermission(
    PERMISSION.VIEW_ORGANIZATION_LIST,
  );
  const hasListingStages = listingStages.length > 0;
  const isFilterSelectionReady =
    (hasEditorChoice || hasListingStages) && isFilterMappingReady;

  return useQuery({
    queryKey: [
      QueryKey.CompaniesKpi,
      'spotlight',
      normalizedSearch,
      Boolean(normalizedSearch),
      filters.isEditorChoice ?? false,
      listingStages.join(','),
    ],
    queryFn: () =>
      fetchSpotlightCompaniesKpis({
        ...(filters.isEditorChoice ? { isEditorChoice: true } : {}),
        ...(hasListingStages ? { listingStages } : {}),
        ...(normalizedSearch ? { search: normalizedSearch } : {}),
      }),
    enabled: hasCompanyListPermission && isFilterSelectionReady,
    staleTime: getTimeInMs(5, 'minute'),
  });
};
